import { useCallback, useEffect, useRef, useState } from "react";
import type { Ref } from "react";
import { useLocation } from "react-router-dom";
import type { Appointment, BookingRequest } from "@dentacore/shared-types";

import { listAwaitingValidation, validateAppointment } from "../api/appointments";
import {
  approveBookingRequest,
  declineBookingRequest,
  listBookingRequests,
} from "../api/booking-requests";
import { useWaiting } from "../shell/waiting-context";
import { Pager, useFitPages, useRoomBelow } from "../ui/paging";
import { HelpTip } from "../ui/popovers";
import { clockAt, localDayOf } from "./grid-geometry";
import styles from "./request-queue.module.css";
import { formatPhone } from "../ui/countries";
import { CriticalAlerts } from "../patients/critical-alerts";

/**
 * **Everything waiting on a human from WhatsApp** — the panel the header chip
 * opens (STORY-057, decision HC3).
 *
 * ── Two queues, because ADR-0005 made them two ──────────────────────────────
 *
 * *Awaiting validation* holds **real appointments** the agent booked when a
 * patient confirmed a time. They hold their chairs already; validating records
 * that somebody here has seen one. *Reschedule requests* holds **proposals**
 * that hold nothing; approving one moves a booking a patient may have arranged
 * their day around, which is why it waits for a human at all (P3 Q4).
 *
 * The chip above counts both as one number, because *is there anything* is the
 * question a glance asks. This is where there is room to say which, so it does.
 *
 * ── The bookings list is not bound to the day on screen ─────────────────────
 *
 * This panel sits under the day grid and everything around it is about today,
 * which makes the exception worth stating: `listAwaitingValidation` returns
 * **every unvalidated booking in the branch, whenever it falls**. The chip
 * counts a booking next Tuesday, so the list the chip opens has to be able to
 * show it — otherwise the header names something no screen can reach.
 *
 * That is also why each row carries a **date** and not just a clock.
 *
 * ── Validate here; decline over there ───────────────────────────────────────
 *
 * The safe act is inline, because it is the common one and a queue exists to be
 * cleared. **Declining is not**: it cancels a booking the patient believes they
 * have, so it stays on the appointment panel where the whole visit is on screen
 * and the reason box guards it. *Open* lands you there — it moves the calendar
 * to that booking's own day and selects it. One implementation of declining,
 * and the destructive act keeps its context.
 */
export function RequestQueue({
  branchId,
  timezone,
  onDecided,
  onOpen,
}: {
  branchId: string | null;
  /** The branch's own zone. Times are the ones the patient will turn up at. */
  timezone: string;
  /** A decision may have moved a booking, so the day needs re-reading. */
  onDecided: () => void;
  /** Take the calendar to this booking's day and select it. */
  onOpen: (day: string, appointmentId: string) => void;
}): React.JSX.Element | null {
  const { refresh } = useWaiting();
  const location = useLocation();
  const panel = useRef<HTMLElement>(null);

  const [requests, setRequests] = useState<BookingRequest[] | null>(null);
  const [awaiting, setAwaiting] = useState<Appointment[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      Promise.all([
        // **No `kind` filter any more.** It was `RESCHEDULE`, and the header
        // count is over every pending request — so a request of another kind
        // would have been counted in the chip and missing from the list it
        // opens. One of the two had to give, and a queue that hides something
        // is worse than a queue that shows something rare.
        listBookingRequests({ state: "PENDING" }, signal),
        listAwaitingValidation(signal),
      ])
        .then(([pending, unvalidated]) => {
          setRequests(pending);
          setAwaiting(unvalidated);
        })
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          // Not swallowed: an empty queue and a refused read look identical on
          // screen, and the second is a fault worth seeing.
          setRequests([]);
          setAwaiting([]);
          setError(cause instanceof Error ? cause.message : "Could not read the queue.");
        }),
    [],
  );

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    setRequests(null);
    setAwaiting(null);
    void reload(controller.signal);

    return () => controller.abort();
  }, [branchId, reload]);

  const total = (awaiting?.length ?? 0) + (requests?.length ?? 0);
  const reading = awaiting === null || requests === null;

  /**
   * The chip lands here (HC3), and React Router does not scroll to a hash.
   *
   * ── Why it waits for the lists, and why that is not belt-and-braces ────────
   *
   * The first version scrolled as soon as the hash appeared, and **it did
   * nothing at all** — found by looking at the running page, after the test
   * that was supposed to cover it passed. Two reasons, and both had to be
   * fixed: the panel is a few lines tall while it still says *reading…*, so
   * scrolling it into view puts it somewhere the finished panel is not; and
   * the calendar's own layout settles after mount, taking the scroll with it.
   *
   * So it waits for both lists, and then waits one frame more for layout. The
   * scroller is the shell's `<main>` rather than the window, which
   * `scrollIntoView` handles and `window.scrollTo` would not.
   *
   * **And it lands instantly rather than smoothly**, which was the second half
   * of the same fault. A smooth scroll is a ~300ms animation that anything
   * re-rendering can cancel, and the day grid settles itself right underneath
   * this one: measured in the running app, it stopped 105px into a 474px
   * journey and left the panel just below the fold — which looks exactly like
   * not scrolling at all. This is a navigation destination, not a flourish.
   *
   * Keyed on `location.key` as well as the hash so that clicking the chip while
   * already parked on `#waiting` scrolls again — the hash alone does not change
   * on a repeat navigation, and a chip that silently does nothing the second
   * time is worse than one that does nothing at all.
   */
  useEffect(() => {
    if (location.hash !== "#waiting" || reading) return;

    const frame = requestAnimationFrame(() => {
      panel.current?.scrollIntoView({ block: "start" });
    });

    return () => cancelAnimationFrame(frame);
  }, [location.hash, location.key, reading]);

  /**
   * **Pages, never a scroll** (Emile, 2026-10-17: *"no infinite scroll please,
   * pagination"*). The queue sits in the calendar's side panel, which grows
   * with what it holds, so the budget is the room left between the list's top
   * and the bottom of the content area — not the list's own height, which would
   * be measuring the rows. A page is however many whole entries fit it, each
   * section's heading counted as an entry.
   */
  const entries: QueueEntry[] = [];
  if ((awaiting ?? []).length > 0)
    entries.push({ kind: "head", section: "awaiting", key: "head-awaiting" });
  for (const appointment of awaiting ?? []) {
    entries.push({
      kind: "awaiting",
      section: "awaiting",
      key: `a-${appointment.id}`,
      appointment,
    });
  }
  if ((requests ?? []).length > 0)
    entries.push({ kind: "head", section: "requests", key: "head-requests" });
  for (const request of requests ?? []) {
    entries.push({ kind: "request", section: "requests", key: `r-${request.id}`, request });
  }
  const [roomRef, room] = useRoomBelow(HEAD_ROOM + PAGER_ROOM);
  const { areaRef, measure, page, setPage, pages, range } = useFitPages({
    keys: entries.map((entry) => entry.key),
    estimate: (index) => {
      const kind = entries[index]?.kind;
      return kind === "head" ? HEAD_ROOM : kind === "request" ? 120 : 96;
    },
    gap: 0,
    resetOn: branchId,
    budget: room,
  });

  if (branchId === null) return null;

  /** Both queues change the count, so both re-read it (HC2). */
  const settle = (work: Promise<unknown>, failure: string): void => {
    work
      .then(() => {
        onDecided();
        refresh();
        return reload();
      })
      .catch((cause: unknown) => {
        // The server's sentence, verbatim — it names what got in the way and,
        // for opening hours, the branch's own times.
        setError(cause instanceof Error ? cause.message : failure);
        // Re-read regardless: whatever refused it may have changed the queue.
        refresh();
        return reload();
      })
      .finally(() => setBusy(null));
  };

  const decide = (request: BookingRequest, decision: "approve" | "decline"): void => {
    setBusy(request.id);
    setError(null);

    settle(
      decision === "approve"
        ? approveBookingRequest(request.id)
        : declineBookingRequest(request.id),
      "That decision did not save.",
    );
  };

  const validate = (appointment: Appointment): void => {
    setBusy(appointment.id);
    setError(null);
    settle(validateAppointment(appointment.id), "That validation did not save.");
  };

  const awaitingHead = (key: string, ref?: Ref<HTMLParagraphElement>): React.JSX.Element => (
    <p key={key} ref={ref} className={styles.section} data-testid="queue-section-awaiting">
      Awaiting validation{" "}
      <HelpTip label="About awaiting validation">Booked already — holding the chair.</HelpTip>
    </p>
  );
  /*
    **"Requests", not "Reschedule requests".** The list dropped its `kind`
    filter so it can show everything the chip counts, and that includes
    requests for a new time — conversations that never reached a confirmation
    (ADR-0005). Leaving the old heading over them would have been a queue
    quietly mislabelling half its contents.
  */
  const requestsHead = (key: string, ref?: Ref<HTMLParagraphElement>): React.JSX.Element => (
    <p key={key} ref={ref} className={styles.section} data-testid="queue-section-requests">
      Requests <HelpTip label="About requests">Nothing changes until you agree.</HelpTip>
    </p>
  );
  const awaitingRowOf = (
    appointment: Appointment,
    key: string,
    ref: Ref<HTMLDivElement>,
  ): React.JSX.Element => (
    <div key={key} ref={ref} className={styles.row} data-testid="awaiting-row">
      <div className={styles.who}>
        <span className={styles.name}>
          {appointment.patient === null
            ? "Chair held"
            : `${appointment.patient.firstName} ${appointment.patient.lastName}`}
        </span>
        {/*
          **The date, not only the clock.** These are not bound to the
          day on screen, so "14:00" alone would be read as this
          afternoon whichever Tuesday it actually is.
        */}
        {/*
          **`6 Sep · 14:00`, not `2026-09-06 · 14:00`.** The ISO date is
          what the branch's timezone resolves to and it went straight to
          the screen — a machine's spelling of a date, in a rail where it
          then wrapped to two lines. The year is dropped because
          everything in this queue is imminent by construction.
        */}
        {/*
          `data-day` carries the ISO date the text no longer spells out.
          The day is still the fact this row is about — it decides where
          *Open* takes you — so it stays readable by a machine while the
          human reads "6 Sep".
        */}
        <span
          className={styles.when}
          data-testid="awaiting-when"
          data-day={localDayOf(appointment.startsAt, timezone)}
        >
          {shortDay(localDayOf(appointment.startsAt, timezone))} ·{" "}
          {clockAt(appointment.startsAt, timezone)}
        </span>
      </div>

      {(appointment.procedureSummary ?? appointment.procedureLabel) !== null && (
        <span className={styles.what}>
          {appointment.procedureSummary ?? appointment.procedureLabel}
        </span>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.approve}
          disabled={busy !== null}
          onClick={() => validate(appointment)}
          data-testid="queue-validate"
        >
          Validate
        </button>
        <button
          type="button"
          className={styles.decline}
          disabled={busy !== null}
          // Declining lives on the appointment panel, behind its reason
          // box. This takes the reader there rather than growing a
          // second decline path with a second confirmation to keep right.
          onClick={() => onOpen(localDayOf(appointment.startsAt, timezone), appointment.id)}
          data-testid="queue-open"
        >
          Open
        </button>
      </div>
    </div>
  );
  const requestRowOf = (
    request: BookingRequest,
    key: string,
    ref: Ref<HTMLDivElement>,
  ): React.JSX.Element => {
    const moving = request.reschedules;
    const blocked = moving !== null && !moving.isMovable;
    /**
     * **A request for a new time has no approve step**, and the server
     * refuses one — `booking-requests.spec.ts` has asserted that since
     * STORY-035b. Under ADR-0005 a patient who *confirms* a time gets a
     * real booking, so what reaches this queue as "new" is the
     * conversation that did not: an unavailable time, an ambiguity, an
     * escalation. Those are answered by booking something, not by
     * approving a row.
     *
     * It is still shown and still declinable, which is what keeps the
     * header count honest — a chip that counted a row no screen could
     * clear would only ever grow.
     */
    const isNew = moving === null;

    return (
      <div key={key} ref={ref} className={styles.row} data-testid="request-row">
        <div className={styles.who}>
          <span className={styles.name}>
            {request.patientName ?? formatPhone(request.phoneE164)}
          </span>
          <span className={styles.when} data-testid="request-move">
            {moving === null
              ? "a new appointment"
              : `${clockAt(moving.startsAt, timezone)} → ${
                  request.proposedStartsAt === null
                    ? "an unstated time"
                    : clockAt(request.proposedStartsAt, timezone)
                }`}
          </span>
        </div>

        {request.requestedProcedure !== null && (
          <span className={styles.what}>{request.requestedProcedure}</span>
        )}

        {/* Approving one is booking it (PP-D1): the patient's critical alerts, shown. */}
        <CriticalAlerts patientId={request.patientId} testId="request-critical" />

        {/*
          §6.1 fixes completed and cancelled bookings in place, so a request
          naming one can never be approved. `isMovable` is computed
          server-side — this only stops the button pretending.
        */}
        {blocked && (
          <span className={styles.blocked} data-testid="request-blocked">
            That visit is {moving?.status.toLowerCase().replace("_", " ")} and can no longer move.
            Decline it, and offer the patient a new booking.
          </span>
        )}

        {isNew && (
          <span className={styles.blocked} data-testid="request-new">
            A time this patient asked for, rather than a move. Book it on the calendar and then
            decline this — there is no approve step for a new booking.
          </span>
        )}

        <div className={styles.actions}>
          {/*
            Absent rather than disabled for a new request. A greyed-out
            "Approve the move" over a request that is not a move reads
            as something broken; the sentence above says what to do.
          */}
          {!isNew && (
            <button
              type="button"
              className={styles.approve}
              disabled={busy !== null || blocked}
              onClick={() => decide(request, "approve")}
              data-testid="request-approve"
            >
              Approve the move
            </button>
          )}
          <button
            type="button"
            className={styles.decline}
            disabled={busy !== null}
            onClick={() => decide(request, "decline")}
            data-testid="request-decline"
          >
            Decline
          </button>
        </div>
      </div>
    );
  };

  const shown = entries.slice(range[0], range[1]);
  const first = shown[0];
  // A page that starts inside a section repeats its heading, unmeasured: the
  // budget kept a heading's room for it.
  const repeat =
    first !== undefined && first.kind !== "head"
      ? first.section === "awaiting"
        ? awaitingHead("repeat-awaiting")
        : requestsHead("repeat-requests")
      : null;

  return (
    <section className={styles.panel} data-testid="request-queue" id="waiting" ref={panel}>
      <div className={styles.head}>
        <span className={styles.title}>Waiting on WhatsApp</span>
        {/* The number alone — see the same note in `waitlist-strip.tsx`. */}
        <span className={styles.hint} title="Waiting on a decision" data-testid="queue-count">
          {reading ? "…" : String(total)}
        </span>
      </div>

      {error !== null && (
        <p className={styles.error} data-testid="request-queue-error">
          {error}
        </p>
      )}

      {!reading && total === 0 && (
        <p className={styles.empty} data-testid="request-queue-empty">
          Nothing waiting.{" "}
          <HelpTip label="About waiting on WhatsApp">
            Bookings the WhatsApp assistant makes appear here for a glance, and patients ask to move
            an appointment the same way.
          </HelpTip>
        </p>
      )}

      {entries.length > 0 && (
        // Down to the content area's bottom, so the card ends where the day does.
        <div
          className={styles.list}
          ref={roomRef}
          style={room > 0 ? { minHeight: room + HEAD_ROOM } : undefined}
        >
          <div ref={areaRef}>
            {repeat}
            {shown.map((entry) =>
              entry.kind === "head"
                ? entry.section === "awaiting"
                  ? awaitingHead(entry.key, measure(entry.key))
                  : requestsHead(entry.key, measure(entry.key))
                : entry.kind === "awaiting"
                  ? awaitingRowOf(entry.appointment, entry.key, measure(entry.key))
                  : requestRowOf(entry.request, entry.key, measure(entry.key)),
            )}
          </div>
        </div>
      )}
      {/* A page, never a scroll (Emile, 2026-10-17: "no infinite scroll please"). */}
      <Pager
        compact
        page={page}
        count={pages.length}
        onPage={setPage}
        from={range[0] + 1}
        to={range[1]}
        total={entries.length}
        noun="waiting"
        testId="request-queue-pager"
      />
    </section>
  );
}

type QueueEntry =
  | { kind: "head"; section: "awaiting" | "requests"; key: string }
  | { kind: "awaiting"; section: "awaiting"; key: string; appointment: Appointment }
  | { kind: "request"; section: "requests"; key: string; request: BookingRequest };

/** A section heading's height, kept back for the one a mid-section page repeats. */
const HEAD_ROOM = 30;
/** The compact pager and the card's bottom padding, under the list. */
const PAGER_ROOM = 40;

/**
 * `2026-09-06` → `6 Sep`.
 *
 * A date a person reads rather than one a database stores. The year is dropped
 * on purpose: everything in this queue is a booking somebody is waiting on a
 * decision about, so it is days away at most, and the year is the part that
 * pushed the line onto two.
 */
function shortDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
}
